import {
  ArrowRight,
  BarChart3,
  ClipboardList,
  FileCheck2,
  Lock,
  Paperclip,
  ShieldCheck,
  Smartphone,
  Users,
} from "lucide-react";
import { ROLE_LABELS } from "@academichub/shared";
import { Card } from "./ui";

/**
 * Panel penjelasan pada halaman login: ringkasan fungsi aplikasi, alur validasi,
 * dan lingkup akses setiap peran — supaya pengguna baru memahami sistem sebelum masuk.
 */

const FEATURES = [
  {
    icon: <ClipboardList className="size-4" aria-hidden />,
    title: "Pencatatan kegiatan",
    description:
      "Catat kegiatan beserta kategori, penyelenggara, penanggung jawab, luaran, dan tautan pendukung.",
  },
  {
    icon: <Paperclip className="size-4" aria-hidden />,
    title: "Bukti dukung",
    description:
      "Unggah sertifikat, laporan, undangan, daftar hadir, surat tugas, publikasi, atau foto dokumentasi (PDF/gambar, maksimal 15 MB per berkas).",
  },
  {
    icon: <FileCheck2 className="size-4" aria-hidden />,
    title: "Alur validasi berjenjang",
    description:
      "Draf diajukan, ditelaah, lalu diputuskan oleh admin program studi, fakultas, atau institut.",
  },
  {
    icon: <Users className="size-4" aria-hidden />,
    title: "Isolasi antar-program-studi",
    description:
      "Setiap pengguna hanya melihat data dalam lingkup wewenangnya. Data di luar lingkup dijawab “tidak ditemukan” agar keberadaannya tidak bocor.",
  },
  {
    icon: <BarChart3 className="size-4" aria-hidden />,
    title: "Dasbor dan laporan",
    description:
      "Statistik lingkup Anda, distribusi kategori, tren enam bulan, serta rekap yang siap diunduh sebagai CSV.",
  },
  {
    icon: <Smartphone className="size-4" aria-hidden />,
    title: "Dapat dipasang di ponsel",
    description:
      "Aplikasi web progresif (PWA) — dapat ditambahkan ke layar utama dan tetap ringan dipakai lewat seluler.",
  },
];

const WORKFLOW: Array<{ label: string; hint: string }> = [
  { label: "Draf", hint: "Disusun pemilik kegiatan" },
  { label: "Diajukan", hint: "Masuk antrean validasi" },
  { label: "Ditelaah", hint: "Ditinjau validator" },
  { label: "Disetujui", hint: "Bukti lengkap dan sah" },
];

const SCOPE: Array<{ role: keyof typeof ROLE_LABELS; scope: string }> = [
  { role: "INSTITUT_ADMIN", scope: "Seluruh institusi" },
  { role: "FAKULTAS_ADMIN", scope: "Semua program studi dalam fakultasnya" },
  { role: "PRODI_ADMIN", scope: "Program studinya dan berwenang memvalidasi" },
  { role: "DOSEN", scope: "Kegiatan miliknya atau yang sudah disetujui" },
  { role: "MAHASISWA", scope: "Kegiatan miliknya atau yang sudah disetujui" },
  { role: "UNIT_OPERATOR", scope: "Program studi dan unit kerjanya" },
];

export function LoginInfo() {
  return (
    <div className="space-y-4">
      <Card className="space-y-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Tentang ISTN Activity Hub</h2>
          <p className="mt-1 text-sm text-slate-600">
            Sistem terpadu untuk mencatat, mengurasi, dan memvalidasi kegiatan seluruh fakultas dan
            program studi di Institut Sains dan Teknologi Nasional beserta bukti dukungnya. Setiap
            program studi mengelola datanya sendiri, sedangkan pimpinan memperoleh gambaran kegiatan
            yang sudah tervalidasi.
          </p>
        </div>

        <ul className="space-y-3">
          {FEATURES.map((feature) => (
            <li key={feature.title} className="flex gap-3">
              <span className="mt-0.5 shrink-0 rounded-xl bg-brand-50 p-2 text-brand-700">
                {feature.icon}
              </span>
              <span>
                <span className="block text-sm font-medium text-slate-800">{feature.title}</span>
                <span className="block text-xs leading-relaxed text-slate-600">
                  {feature.description}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </Card>

      <Card className="space-y-3">
        <h2 className="text-base font-semibold text-slate-900">Alur validasi kegiatan</h2>
        <ol className="space-y-2">
          {WORKFLOW.map((step, index) => (
            <li key={step.label} className="flex items-center gap-2 text-sm">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-600 text-xs font-semibold text-white">
                {index + 1}
              </span>
              <span className="font-medium text-slate-800">{step.label}</span>
              <ArrowRight className="size-3.5 shrink-0 text-slate-400" aria-hidden />
              <span className="text-xs text-slate-600">{step.hint}</span>
            </li>
          ))}
        </ol>
        <p className="rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600 ring-1 ring-slate-200">
          Kegiatan yang ditolak dapat direvisi menjadi draf lalu diajukan kembali. Validator tidak
          boleh memvalidasi kegiatannya sendiri, dan penolakan wajib menyertakan alasan.
        </p>
      </Card>

      <Card className="space-y-3">
        <h2 className="text-base font-semibold text-slate-900">Lingkup akses per peran</h2>
        <dl className="space-y-2">
          {SCOPE.map((item) => (
            <div key={item.role} className="text-sm">
              <dt className="font-medium text-slate-800">{ROLE_LABELS[item.role]}</dt>
              <dd className="text-xs text-slate-600">{item.scope}</dd>
            </div>
          ))}
        </dl>
      </Card>

      <Card className="space-y-2">
        <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
          <ShieldCheck className="size-4 text-brand-600" aria-hidden />
          Keamanan dan data
        </h2>
        <ul className="space-y-1.5 text-xs text-slate-600">
          <li className="flex gap-2">
            <Lock className="mt-0.5 size-3.5 shrink-0 text-slate-400" aria-hidden />
            Sesi memakai cookie httpOnly + SameSite=Strict, token CSRF pada setiap perubahan data,
            dan rotasi refresh token dengan deteksi pemakaian ulang.
          </li>
          <li className="flex gap-2">
            <Paperclip className="mt-0.5 size-3.5 shrink-0 text-slate-400" aria-hidden />
            Berkas bukti diperiksa dari isi (magic byte), bukan ekstensi, dan disimpan di luar
            direktori web.
          </li>
          <li className="flex gap-2">
            <ShieldCheck className="mt-0.5 size-3.5 shrink-0 text-slate-400" aria-hidden />
            Login dibatasi per alamat IP dan dikunci berjenjang setelah beberapa kali gagal. Semua
            tindakan penting tercatat pada audit log.
          </li>
        </ul>
      </Card>
    </div>
  );
}
